/*
 * Editorial media gallery controller for the production PDP migration.
 *
 * Use this file for the preview-safe main product section that keeps the live
 * product-info block stack, while swapping the old left-side media gallery for
 * the new grouped editorial gallery + shared lightbox behavior.
 *
 * Why this file exists:
 * - keeps variant-driven gallery grouping out of the legacy Dawn media-gallery
 * - lets the new metafield image groups react to the live PDP variant picker
 * - reuses the shared lightbox styling with pinch-to-zoom + drag-to-pan
 */

(function () {
  class EditorialProductMediaGallery {
    constructor(root) {
      this.root = root;
      this.productInfo = root.closest('product-info');
      this.slideContainer = root.querySelector('[data-editorial-gallery-slides]');
      this.slides = Array.from(root.querySelectorAll('[data-editorial-slide]'));
      this.mediaWrapper = root.closest('.product__media-wrapper') || root;
      this.variantSelectors = this.productInfo?.querySelector('variant-radios, variant-selects') || null;
      this.variantData = this.parseJson(root.querySelector('[data-product-variants-json]'));
      this.optionNames = this.parseJson(root.querySelector('[data-option-names-json]'));
      this.galleryGroups = this.parseJson(root.querySelector('[data-gallery-groups-json]'));
      this.colorOptionName = root.dataset.colorOptionName || 'Color';
      this.imageZoomMode = root.dataset.imageZoom || 'lightbox';
      this.mobileMediaQuery = window.matchMedia('(max-width: 989px)');
      this.scrollFrame = 0;
      this.currentMediaIds = [];
      this.activeMediaId = '';

      this.modelInfo = this.productInfo?.querySelector('.product-model-info') || null;
      this.modelInfoDetailItems = Array.from(
        this.productInfo?.querySelectorAll('.product-model-info [data-model-info-details]') || []
      );

      this.lightbox = root.querySelector('[data-gallery-lightbox]');
      this.lightboxStage = root.querySelector('[data-gallery-lightbox-stage]');
      this.lightboxSlides = root.querySelector('[data-gallery-lightbox-slides]');
      this.lightboxOpen = false;
      this.lightboxDefaultScale = 1.65;
      this.lightboxMinScale = 1;
      this.lightboxMaxScale = 4;
      this.lightboxScale = this.lightboxDefaultScale;
      this.lightboxTranslate = { x: 0, y: 0 };
      this.lightboxPointerId = null;
      this.lightboxDragOrigin = { x: 0, y: 0 };
      this.lightboxDragStart = { x: 0, y: 0 };
      this.lightboxGesturePointers = new Map();
      this.lightboxGestureStartDistance = 0;
      this.lightboxGestureStartScale = this.lightboxDefaultScale;
      this.lightboxGestureStartTranslate = { x: 0, y: 0 };
      this.lightboxGestureStartCenter = { x: 0, y: 0 };

      if (!this.slideContainer || !this.slides.length) return;

      this.root.setActiveMedia = this.setActiveMedia;
      this.root.editorialGalleryController = this;

      // Always start collapsed. This protects against any browser/theme state
      // that might leave <details> open across re-renders or delayed hydration.
      this.modelInfo?.removeAttribute('open');

      this.bindEvents();
      this.paintSlideCursor();
      this.refreshFromSelection({ syncScroll: false });
    }

    parseJson(node) {
      if (!node) return [];

      try {
        return JSON.parse(node.textContent.trim());
      } catch (error) {
        console.warn('Editorial gallery JSON parse failed', error);
        return [];
      }
    }

    bindEvents() {
      this.variantSelectors?.addEventListener('change', this.handleVariantChange);
      this.slideContainer.addEventListener('click', this.handleSlideClick);
      this.slideContainer.addEventListener('scroll', this.handleSlideScroll, { passive: true });
      this.root.querySelectorAll('[data-gallery-close-lightbox]').forEach((node) => {
        node.addEventListener('click', this.handleCloseLightboxClick);
      });
      this.productInfo?.querySelectorAll('[data-model-info-close]').forEach((node) => {
        node.addEventListener('click', this.handleModelInfoClose);
      });
      this.lightboxStage?.addEventListener('pointerdown', this.handleLightboxPointerDown);
      this.lightboxStage?.addEventListener('pointermove', this.handleLightboxPointerMove);
      this.lightboxStage?.addEventListener('pointerup', this.handleLightboxPointerUp);
      this.lightboxStage?.addEventListener('pointercancel', this.handleLightboxPointerUp);
      document.addEventListener('keydown', this.handleDocumentKeydown);
    }

    getSelectedOptions() {
      if (!this.variantSelectors) return [];

      if (this.variantSelectors.tagName.toLowerCase() === 'variant-radios') {
        return Array.from(this.variantSelectors.querySelectorAll('fieldset')).map((fieldset) => {
          return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value || '';
        });
      }

      return Array.from(this.variantSelectors.querySelectorAll('select')).map((select) => select.value);
    }

    getCurrentVariant() {
      const selectedOptions = this.getSelectedOptions();
      const fallbackVariantId = String(this.root.dataset.initialVariantId || '');

      return (
        this.variantData.find((variant) => {
          return variant.options.every((optionValue, index) => selectedOptions[index] === optionValue);
        }) || this.variantData.find((variant) => String(variant.id) === fallbackVariantId) || null
      );
    }

    getSelectedColor() {
      const colorOptionIndex = this.optionNames.findIndex((optionName) => optionName === this.colorOptionName);
      if (colorOptionIndex < 0) return '';

      const currentVariant = this.getCurrentVariant();
      return currentVariant?.options?.[colorOptionIndex] || '';
    }

    getSlideByMediaId(mediaId) {
      return this.slides.find((slide) => String(slide.dataset.mediaBaseId) === String(mediaId));
    }

    getAllMediaIds() {
      return this.slides.map((slide) => String(slide.dataset.mediaBaseId));
    }

    getGroupedMediaIds() {
      const selectedColor = this.getSelectedColor();
      if (!selectedColor) return [];

      const match = this.galleryGroups.find((group) => {
        return group.optionValue === selectedColor || group.label === selectedColor;
      });

      if (!match?.mediaIds?.length) return [];

      const existingMediaIds = new Set(this.getAllMediaIds());
      return match.mediaIds.map(String).filter((mediaId) => existingMediaIds.has(mediaId));
    }

    getFallbackMediaIds() {
      return this.getAllMediaIds();
    }

    resolveCurrentMediaIds() {
      const groupedMediaIds = this.getGroupedMediaIds();
      return groupedMediaIds.length ? groupedMediaIds : this.getFallbackMediaIds();
    }

    syncSlides() {
      const visibleSet = new Set(this.currentMediaIds);

      this.currentMediaIds.forEach((mediaId) => {
        const slide = this.getSlideByMediaId(mediaId);
        if (!slide) return;

        this.slideContainer.appendChild(slide);
        slide.hidden = false;
        slide.setAttribute('aria-hidden', 'false');
      });

      this.slides.forEach((slide) => {
        const isVisible = visibleSet.has(String(slide.dataset.mediaBaseId));
        slide.hidden = !isVisible;
        slide.setAttribute('aria-hidden', String(!isVisible));
      });
    }

    setActiveMediaId(mediaId, { syncScroll = true } = {}) {
      if (!mediaId) return;

      this.activeMediaId = String(mediaId);
      this.slides.forEach((slide) => {
        slide.classList.toggle('is-active', String(slide.dataset.mediaBaseId) === this.activeMediaId && !slide.hidden);
      });

      if (syncScroll && this.mobileMediaQuery.matches) {
        const activeSlide = this.getSlideByMediaId(this.activeMediaId);
        if (activeSlide) {
          this.slideContainer.scrollTo({ left: activeSlide.offsetLeft, behavior: 'smooth' });
        }
      }
    }

    refreshFromSelection({ syncScroll = true } = {}) {
      this.currentVariant = this.getCurrentVariant();
      this.currentMediaIds = this.resolveCurrentMediaIds();
      this.syncSlides();
      this.syncModelInfo();

      const featuredMediaId = String(this.currentVariant?.featured_media?.id || '');
      const nextActiveMediaId = this.currentMediaIds.includes(featuredMediaId)
        ? featuredMediaId
        : this.currentMediaIds[0] || '';

      this.setActiveMediaId(nextActiveMediaId, { syncScroll });

      if (this.lightboxOpen) {
        this.rebuildLightboxSlides(this.activeMediaId);
      }
    }

    getNearestVisibleMediaIdFromScroll() {
      let nearestMediaId = this.activeMediaId;
      let smallestDistance = Number.POSITIVE_INFINITY;

      this.currentMediaIds.forEach((mediaId) => {
        const slide = this.getSlideByMediaId(mediaId);
        if (!slide || slide.hidden) return;

        const distance = Math.abs(slide.offsetLeft - this.slideContainer.scrollLeft);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          nearestMediaId = String(mediaId);
        }
      });

      return nearestMediaId;
    }

    isColorChangeEvent(target) {
      if (!target) return false;

      const controlName = target.getAttribute('name') || '';
      return controlName === this.colorOptionName || controlName.includes(`[${this.colorOptionName}]`);
    }

    shouldScrollBackToGallery() {
      if (!this.mediaWrapper) return false;

      const galleryRect = this.mediaWrapper.getBoundingClientRect();
      return galleryRect.top < -120;
    }

    scrollBackToGallery() {
      if (!this.mediaWrapper) return;

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const targetTop = window.scrollY + this.mediaWrapper.getBoundingClientRect().top - 80;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    handleVariantChange = (event) => {
      const shouldScrollBack = this.isColorChangeEvent(event.target) && this.shouldScrollBackToGallery();

      window.requestAnimationFrame(() => {
        this.refreshFromSelection();

        if (!shouldScrollBack) return;

        window.requestAnimationFrame(() => {
          this.scrollBackToGallery();
        });
      });
    };

    syncModelInfo() {
      if (!this.modelInfoDetailItems.length) return;

      const activeVariantId = String(this.currentVariant?.id || '');
      const visibleDetailItem =
        this.modelInfoDetailItems.find((item) => String(item.dataset.variantId) === activeVariantId) ||
        this.modelInfoDetailItems[0] ||
        null;

      this.modelInfoDetailItems.forEach((item) => {
        item.style.display = item === visibleDetailItem ? 'inline' : 'none';
      });

      if (this.modelInfo) {
        this.modelInfo.style.display = visibleDetailItem ? '' : 'none';
      }
    }

    paintSlideCursor() {
      this.slides.forEach((slide) => {
        const isImageSlide = slide.dataset.mediaType === 'image';
        const cursor = this.imageZoomMode === 'lightbox' && isImageSlide ? 'zoom-in' : 'default';
        slide.style.cursor = cursor;
      });
    }

    handleSlideClick = (event) => {
      const slide = event.target.closest('[data-editorial-slide]');
      if (!slide || slide.hidden) return;
      if (this.imageZoomMode !== 'lightbox') return;
      if (slide.dataset.mediaType !== 'image') return;

      this.openLightboxForMediaId(slide.dataset.mediaBaseId);
    };

    handleSlideScroll = () => {
      if (!this.mobileMediaQuery.matches || this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        const nearestMediaId = this.getNearestVisibleMediaIdFromScroll();
        if (!nearestMediaId || nearestMediaId === this.activeMediaId) return;
        this.setActiveMediaId(nearestMediaId, { syncScroll: false });
      });
    };

    rebuildLightboxSlides(activeMediaId) {
      if (!this.lightboxSlides) return;

      this.lightboxSlides.innerHTML = '';

      this.currentMediaIds.forEach((mediaId) => {
        const sourceSlide = this.getSlideByMediaId(mediaId);
        if (!sourceSlide || sourceSlide.dataset.mediaType !== 'image') return;

        const sourceImage = sourceSlide.querySelector('img');
        if (!sourceImage) return;

        const slide = document.createElement('div');
        slide.className = 'product-gallery-lightbox__slide';
        slide.dataset.mediaId = String(mediaId);
        slide.setAttribute('data-gallery-lightbox-slide', '');
        if (String(mediaId) === String(activeMediaId)) slide.classList.add('is-active');

        const image = document.createElement('img');
        image.className = 'product-gallery-lightbox__image';
        image.alt = sourceImage.alt || '';
        image.src = sourceSlide.dataset.zoomSrc || sourceImage.currentSrc || sourceImage.src;
        image.draggable = false;

        slide.appendChild(image);
        this.lightboxSlides.appendChild(slide);
      });

      this.resetLightboxPan();
    }

    getActiveLightboxImage() {
      return this.lightboxSlides?.querySelector('.product-gallery-lightbox__slide.is-active img') || null;
    }

    setActiveLightboxMediaId(mediaId) {
      this.lightboxSlides?.querySelectorAll('[data-gallery-lightbox-slide]').forEach((slide) => {
        slide.classList.toggle('is-active', String(slide.dataset.mediaId) === String(mediaId));
      });
      this.resetLightboxPan();
    }

    openLightboxForMediaId(mediaId) {
      if (!this.lightbox) return;

      this.setActiveMediaId(mediaId, { syncScroll: false });
      this.rebuildLightboxSlides(mediaId);
      this.lightbox.hidden = false;
      this.lightboxOpen = true;
      document.documentElement.classList.add('product-gallery-lightbox-open');
      this.lightboxStage?.focus();
    }

    setActiveMedia = (mediaId) => {
      const exactMatch = this.slides.find((slide) => String(slide.dataset.mediaId) === String(mediaId));
      const fallbackMediaId = String(mediaId).split('-').pop();
      const targetMediaId = exactMatch?.dataset.mediaBaseId || fallbackMediaId;
      if (!targetMediaId) return;

      this.refreshFromSelection({ syncScroll: false });
      this.setActiveMediaId(targetMediaId, { syncScroll: false });

      if (this.lightboxOpen) {
        this.setActiveLightboxMediaId(targetMediaId);
      }
    };

    closeLightbox() {
      if (!this.lightbox) return;

      this.lightbox.hidden = true;
      this.lightboxOpen = false;
      this.lightboxPointerId = null;
      this.lightboxGesturePointers.clear();
      this.lightboxStage?.classList.remove('is-dragging');
      document.documentElement.classList.remove('product-gallery-lightbox-open');
      this.resetLightboxPan();
    }

    resetLightboxPan() {
      this.lightboxScale = this.lightboxDefaultScale;
      this.lightboxTranslate = { x: 0, y: 0 };
      this.lightboxGesturePointers.clear();
      this.applyLightboxTransform();
    }

    applyLightboxTransform() {
      const image = this.getActiveLightboxImage();
      if (!image) return;

      image.style.transform = `translate3d(${this.lightboxTranslate.x}px, ${this.lightboxTranslate.y}px, 0) scale(${this.lightboxScale})`;
    }

    getLightboxTranslateBounds() {
      if (!this.lightboxStage) return { maxX: 0, maxY: 0 };

      return {
        maxX: Math.max(0, (this.lightboxStage.clientWidth * (this.lightboxScale - 1)) / 2),
        maxY: Math.max(0, (this.lightboxStage.clientHeight * (this.lightboxScale - 1)) / 2),
      };
    }

    clampLightboxTranslate(nextTranslate) {
      const bounds = this.getLightboxTranslateBounds();
      return {
        x: Math.max(-bounds.maxX, Math.min(bounds.maxX, nextTranslate.x)),
        y: Math.max(-bounds.maxY, Math.min(bounds.maxY, nextTranslate.y)),
      };
    }

    clampLightboxScale(nextScale) {
      return Math.max(this.lightboxMinScale, Math.min(this.lightboxMaxScale, nextScale));
    }

    getPointerDistance(pointerA, pointerB) {
      return Math.hypot(pointerB.x - pointerA.x, pointerB.y - pointerA.y);
    }

    getPointerCenter(pointerA, pointerB) {
      return {
        x: (pointerA.x + pointerB.x) / 2,
        y: (pointerA.y + pointerB.y) / 2,
      };
    }

    updateLightboxPointer(event) {
      this.lightboxGesturePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }

    handleCloseLightboxClick = () => {
      this.closeLightbox();
    };

    handleLightboxPointerDown = (event) => {
      if (!this.lightboxOpen || !this.lightboxStage) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.updateLightboxPointer(event);
      this.lightboxStage.setPointerCapture(event.pointerId);

      if (this.lightboxGesturePointers.size === 1) {
        this.lightboxPointerId = event.pointerId;
        this.lightboxDragOrigin = { x: event.clientX, y: event.clientY };
        this.lightboxDragStart = { ...this.lightboxTranslate };
        this.lightboxStage.classList.add('is-dragging');
      }

      if (this.lightboxGesturePointers.size === 2) {
        const [pointerA, pointerB] = [...this.lightboxGesturePointers.values()];
        this.lightboxPointerId = null;
        this.lightboxStage.classList.remove('is-dragging');
        this.lightboxGestureStartDistance = this.getPointerDistance(pointerA, pointerB);
        this.lightboxGestureStartScale = this.lightboxScale;
        this.lightboxGestureStartTranslate = { ...this.lightboxTranslate };
        this.lightboxGestureStartCenter = this.getPointerCenter(pointerA, pointerB);
      }

      event.preventDefault();
    };

    handleLightboxPointerMove = (event) => {
      if (!this.lightboxOpen) return;
      if (!this.lightboxGesturePointers.has(event.pointerId)) return;

      this.updateLightboxPointer(event);

      if (this.lightboxGesturePointers.size >= 2) {
        const [pointerA, pointerB] = [...this.lightboxGesturePointers.values()];
        const nextDistance = this.getPointerDistance(pointerA, pointerB);
        const nextCenter = this.getPointerCenter(pointerA, pointerB);
        const distanceRatio = this.lightboxGestureStartDistance ? nextDistance / this.lightboxGestureStartDistance : 1;

        this.lightboxScale = this.clampLightboxScale(this.lightboxGestureStartScale * distanceRatio);
        this.lightboxTranslate = this.clampLightboxTranslate({
          x: this.lightboxGestureStartTranslate.x + (nextCenter.x - this.lightboxGestureStartCenter.x),
          y: this.lightboxGestureStartTranslate.y + (nextCenter.y - this.lightboxGestureStartCenter.y),
        });
        this.applyLightboxTransform();
        return;
      }

      if (event.pointerId !== this.lightboxPointerId) return;

      const nextTranslate = {
        x: this.lightboxDragStart.x + (event.clientX - this.lightboxDragOrigin.x),
        y: this.lightboxDragStart.y + (event.clientY - this.lightboxDragOrigin.y),
      };

      this.lightboxTranslate = this.clampLightboxTranslate(nextTranslate);
      this.applyLightboxTransform();
    };

    handleLightboxPointerUp = (event) => {
      if (!this.lightboxStage || !this.lightboxGesturePointers.has(event.pointerId)) return;

      this.lightboxGesturePointers.delete(event.pointerId);

      if (this.lightboxStage.hasPointerCapture(event.pointerId)) {
        this.lightboxStage.releasePointerCapture(event.pointerId);
      }

      if (this.lightboxGesturePointers.size === 1) {
        const [remainingPointerId, remainingPointer] = [...this.lightboxGesturePointers.entries()][0];
        this.lightboxPointerId = remainingPointerId;
        this.lightboxDragOrigin = { x: remainingPointer.x, y: remainingPointer.y };
        this.lightboxDragStart = { ...this.lightboxTranslate };
        this.lightboxStage.classList.add('is-dragging');
        return;
      }

      this.lightboxPointerId = null;
      this.lightboxStage.classList.remove('is-dragging');
    };

    handleDocumentKeydown = (event) => {
      if (!this.lightboxOpen) return;
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.closeLightbox();
    };
  }

  const initEditorialMediaGalleries = (scope = document) => {
    scope.querySelectorAll('[data-editorial-media-gallery]').forEach((root) => {
      if (root.dataset.editorialGalleryInitialized === 'true') return;
      root.dataset.editorialGalleryInitialized = 'true';
      new EditorialProductMediaGallery(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initEditorialMediaGalleries());
  } else {
    initEditorialMediaGalleries();
  }

  document.addEventListener('shopify:section:load', (event) => initEditorialMediaGalleries(event.target));
})();
